import { useState, useEffect } from 'react';
import { Heart, BookOpen, Target, Award, User } from 'lucide-react';
import { API_URL } from '../config/api';
import './About.css';

const About = () => {
    const [content, setContent] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchContent();
        fetchDoctors();
    }, []);

    const getImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `${API_URL}${url}`;
    };

    const fetchContent = async () => {
        try {
            const response = await fetch(`${API_URL}/content/about`);
            const data = await response.json();
            if (data.success) setContent(data.data);
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await fetch(`${API_URL}/site-team`);
            const data = await response.json();
            if (data.success) setDoctors(data.data || []);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center' }}>Cargando...</div>;
    }

    return (
        <div className="about-page">
            {/* Header */}
            <section className="page-header">
                <div className="container">
                    <h1 className="page-title">{content['page_title'] || 'Quiénes Somos'}</h1>
                    <p className="page-subtitle">
                        {content['page_subtitle'] || 'Comprometidos con la excelencia médica y el trato humano en cada diagnóstico genético.'}
                    </p>
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="section container">
                <div className="philosophy-grid">
                    <div className="philosophy-content">
                        <h2 className="section-title" style={{ textAlign: 'left' }}>
                            {content['philosophy_title'] || 'Nuestra Filosofía'}
                        </h2>
                        <p>
                            {content['philosophy_text'] || 'En el Consultorio de Genética Integral, creemos que detrás de cada estudio genético hay una historia, una familia y una esperanza.'}
                        </p>
                        <ul className="philosophy-list">
                            <li><strong>{content['philosophy_point_1'] || 'Trato Humano: Escuchamos y acompañamos con empatía.'}</strong></li>
                            <li><strong>{content['philosophy_point_2'] || 'Ética Profesional: Altos estándares de bioética.'}</strong></li>
                            <li><strong>{content['philosophy_point_3'] || 'Evidencia Científica: Diagnósticos basados en literatura actual.'}</strong></li>
                        </ul>
                    </div>
                    <div className="philosophy-image">
                        <img
                            src={content['philosophy_image'] ? getImageUrl(content['philosophy_image']) : "https://images.unsplash.com/photo-1576091160550-217358c7db81?auto=format&fit=crop&q=80&w=800"}
                            alt={content['philosophy_title'] || "Consulta médica empática"}
                            className="rounded-image shadow-lg"
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                    </div>
                </div>
            </section>

            {/* Mission Vision Values */}
            <section className="section bg-light">
                <div className="container">
                    <div className="mvv-grid">
                        <div className="mvv-card">
                            <Target className="mvv-icon" size={40} />
                            <h3>{content['mission_title'] || 'Misión'}</h3>
                            <p>{content['mission_text'] || 'Proporcionar servicios de genética clínica de alta calidad, accesibles y oportunos.'}</p>
                        </div>
                        <div className="mvv-card">
                            <BookOpen className="mvv-icon" size={40} />
                            <h3>{content['vision_title'] || 'Visión'}</h3>
                            <p>{content['vision_text'] || 'Ser el referente en genética clínica por excelencia diagnóstica e innovación.'}</p>
                        </div>
                        <div className="mvv-card">
                            <Heart className="mvv-icon" size={40} />
                            <h3>{content['values_title'] || 'Valores'}</h3>
                            <p>{content['values_text'] || 'Integridad, Empatía, Excelencia, Responsabilidad e Innovación.'}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Team Section - Dynamic Doctors */}
            <section className="section container">
                <h2 className="section-title">Nuestro Equipo Médico</h2>
                <p className="section-subtitle">Liderado por especialistas certificados en genética médica.</p>

                <div className="doctors-grid">
                    {doctors.length > 0 ? (
                        doctors.map((doctor) => (
                            <div key={doctor.id} className="doctor-card-public">
                                <div className="doctor-photo-public">
                                    {doctor.photo_url ? (
                                        <img src={doctor.photo_url.startsWith('http') ? doctor.photo_url : `${API_URL}${doctor.photo_url}`} alt={doctor.name} />
                                    ) : (
                                        <User size={48} />
                                    )}
                                </div>
                                <h3 className="doctor-name">{doctor.name}</h3>
                                <p className="doctor-specialty">{doctor.specialty}</p>
                                {doctor.bio && <p className="doctor-bio">{doctor.bio}</p>}
                                <div className="doctor-credentials">
                                    <Award size={14} /> Certificado
                                </div>
                            </div>
                        ))
                    ) : (
                        // Fallback to static content if no doctors in DB
                        <div className="team-card">
                            <div className="team-image-wrapper">
                                <img
                                    src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=400"
                                    alt="Doctor"
                                    className="team-image"
                                />
                            </div>
                            <div className="team-info">
                                <h3 className="team-name">{content['team_doctor_name'] || 'Dr. Alejandro Ruiz'}</h3>
                                <p className="team-specialty">{content['team_doctor_specialty'] || 'Médico Genetista Certificado'}</p>
                                <div className="team-bio">
                                    <p>{content['team_doctor_bio_1'] || 'Especialista con amplia experiencia en genética médica.'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default About;

